import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators, compose } from 'redux';
import get from 'lodash/get';
import clsx from 'clsx';
import { PRODUCT_MODELS } from '../util/constants';
import Spinner from '../../components/LazyWidgets/Spinner';
import LeftHandNav from '../../components/LeftHandNav/LeftHandNav';
import QQSubmoduleReport from './components/QuantQual/QuantQual';
import QQModuleReport from './components/QuantQualModule/QuantQualModule';
import SkillsOverviewReport from './components/skillsOverviewReport/SkillsOverviewReport';
import SkillsReflectionSummary from './components/skillsReflectionSummary/SkillsReflectionSummary';
import { getCourse } from '../actions/courses';
import { REPORT_TYPES } from '../actions/constants';
import SkillsFeedbackSummary from './components/skillsFeedbackSummary/SkillsFeedbackSummary';
import SkillsNotReviewedFeedbackSummary from './components/skillsNotReviewedFeedbackSummary/SkillsNotReviewedFeedbackSummary';
import QQStudentUnitReport from './components/QuantQualStudentUnit/QuantQualStudentUnit';
import QQStudentOverviewV2 from './components/QuantQualStudentOverviewV2';
import QQStudentSubunitReport from './components/QuantQualStudentSubunit/QuantQualStudentSubunit';
import RevisionPlansNotStarted from './components/RevisionPlansNotStarted/RevisionPlanNotStarted';
import ReportError from './components/ReportError';
import ReportComingSoon from './components/comingSoon';
import { setCurrentNavigationPath, getCourseAbstract, sendAnalyticsEvent } from '../actions/reports';
import { getCurrentUser } from '../actions/user';
import { viewReport } from '../analyticsEvents';
import { removeParamsFromId, isSectionCourseType } from '../util/util';
import { withLocation } from '../../hoc';
import { getConfigObject } from '../util/config';
import { checkSearchAcrossSectionsEnabled } from '../core/featureCourses';
import s from './style.module.css';

import ReportsTabs from './ReportsTabs';
import ReportsContent from './components/ReportsContent/ReportsContent';
import SectionManagerMessage from './components/SectionManagerMessage';
import SearchSectionsMicroFrontend from './components/SearchSectionsMicroFrontend/SearchSectionsMicroFrontend';

export class ReportsPage extends Component {
  static propTypes = {
    loading: PropTypes.bool,
    match: PropTypes.object,
    location: PropTypes.object,
    onTabChange: PropTypes.func,
    reports: PropTypes.object,
    setCurrentNavigationPath: PropTypes.func,
    sendAnalyticsEvent: PropTypes.func,
    getCourse: PropTypes.func,
    getCurrentUser: PropTypes.func,
    getCourseAbstract: PropTypes.func,
  };

  constructor(props) {
    super(props);
    this.scrollRef = React.createRef();
    this.state = {
      isSearchAcrossSectionsEnabled: false,
    };
  }

  async componentDidMount() {
    if (window.ga) {
      window.ga('set', 'page', 'course/reports');
      window.ga('send', 'pageview');
    }

    const {
      match: {
        params: {
          reportType,
          itemId,
          courseId,
        },
      },
    } = this.props;
    this.props.sendAnalyticsEvent(viewReport(reportType));
    const itemIdNormalized = removeParamsFromId(itemId);
    await Promise.all([
      this.props.getCurrentUser(courseId),
      this.props.getCourse(courseId),
    ]);
    await this.props.getCourseAbstract(courseId, reportType);
    await this.props.setCurrentNavigationPath(reportType, itemIdNormalized);

    // Check if search across sections is enabled
    const { FF_ENABLE_SEARCH_ACROSS_SECTIONS = false } = getConfigObject();
    if (FF_ENABLE_SEARCH_ACROSS_SECTIONS) {
      try {
        const isEnabled = await checkSearchAcrossSectionsEnabled(courseId);
        this.setState({ isSearchAcrossSectionsEnabled: isEnabled });
      } catch (error) {
        console.error('Error checking search across sections:', error);
        this.setState({ isSearchAcrossSectionsEnabled: false });
      }
    }
  }

  async componentDidUpdate(prevProps) {
    const {
      match: {
        params: {
          reportType,
          itemId,
        },
      },
    } = prevProps;

    const {
      match: {
        params: {
          reportType: newReportType,
          itemId: newItemId,
        },
      },
    } = this.props;

    if (reportType !== newReportType) {
      this.props.sendAnalyticsEvent(viewReport(newReportType));
    }
    if (reportType !== newReportType || itemId !== newItemId) {
      this.props.setCurrentNavigationPath(newReportType, newItemId);
    }

    // If the item or subitem changes when switching to the same report, scroll the reports container to the top
    if (this.scrollRef.current &&
        (prevProps.match.params.itemId !== this.props.match.params.itemId
          || prevProps.match.params.subItemId !== this.props.match.params.subItemId)) {
      this.scrollRef.current.scrollTop = 0;
    }
  }

  onTabChange = (tabName, search) => {
    if (this.props.onTabChange) {
      this.props.onTabChange(tabName, search);
      return;
    }

    this.handleNewTab({ tabName, search, fromNavigation: true });
  }

  handleNewTab = ({ tabName, search, isReplace = false }) => {
    const { navigate, match } = this.props;
    const courseId = match.params.id || match.params.courseId;
    // Build the path based on the tab name
    let path = `/courses/${courseId}/${tabName}`;

    // If there's a search query, add it
    if (search) {
      // For React Router v6, navigate automatically handles the search part
      path = `${path}${search}`;
    }

    // Navigate to the new path
    navigate(path, { replace: isReplace });
  }

  render() {
    const {
      location,
      match: {
        params: {
          reportType,
          itemId,
          subItemId,
        },
      },
      reports: {
        courseAbstract: {
          loading,
          id: courseId,
          productModelId,
          type: courseType,
        },
        authorized,
        comingSoon,
        navigationBar,
      },
    } = this.props;

    const { isSearchAcrossSectionsEnabled } = this.state;
    const { FF_ENABLE_SEARCH_ACROSS_SECTIONS = false } = getConfigObject();

    if (!authorized) return null;

    if (isSectionCourseType(courseType)) {
      if (FF_ENABLE_SEARCH_ACROSS_SECTIONS && isSearchAcrossSectionsEnabled) {
        return (
          <div className={s.container}>
            <SearchSectionsMicroFrontend />
          </div>
        );
      }

      return (
        <div className={s.container}>
          <SectionManagerMessage />
        </div>
      );
    }

    if (comingSoon) {
      return (
        <div className={s.container} >
          <ReportComingSoon />
        </div>
      );
    }

    const itemIdNormalized = removeParamsFromId(itemId);
    const subItemNormalized = removeParamsFromId(subItemId);
    if (loading && productModelId === PRODUCT_MODELS.skills) {
      return (
        <div className={s.container} >
          <div className={s.coursePaneMain}>
            <div className={s.loadingWrapper}>
              <div className={s.loadingContainer}>
                <Spinner className={s.mlLoading} />
              </div>
            </div>
          </div>
        </div>
      );
    }

    let reportToShow = null;
    switch (productModelId) {
      case PRODUCT_MODELS.skills:
        switch (reportType) {
          case REPORT_TYPES.skillsFeedbackSummary:
            reportToShow = (<SkillsFeedbackSummary
              onTabChange={this.onTabChange}
              courseId={courseId}
            />);
            break;
          case REPORT_TYPES.skills:
            reportToShow = (<SkillsOverviewReport
              onTabChange={this.onTabChange}
              courseId={courseId}
            />);
            break;
          case REPORT_TYPES.reflections:
            reportToShow = (<SkillsReflectionSummary
              onTabChange={this.onTabChange}
              courseId={courseId}
            />);
            break;
          case REPORT_TYPES.skillsNotReviewedFeedbackSummary:
            reportToShow = (<SkillsNotReviewedFeedbackSummary
              onTabChange={this.onTabChange}
              courseId={courseId}
            />);
            break;
          case REPORT_TYPES.revisionPlansNotStarted:
            reportToShow = (<RevisionPlansNotStarted
              courseId={courseId}
              onTabChange={this.onTabChange}
            />);
            break;
          default:
            reportToShow = <ReportError />;
        }
        break;
      case PRODUCT_MODELS.quantitative:
      case PRODUCT_MODELS.qualitative:
      case PRODUCT_MODELS.goreact:
      case PRODUCT_MODELS.essentials:
        switch (reportType) {
          case REPORT_TYPES.unit:
            reportToShow = (<QQModuleReport
              reportType={reportType}
              unitId={itemIdNormalized}
              onTabChange={this.onTabChange}
              className="qq-report"
            />);
            break;
          case REPORT_TYPES.subunit:
            reportToShow = (<QQSubmoduleReport
              reportType={reportType}
              subUnitId={itemIdNormalized}
              onTabChange={this.onTabChange}
              className="qq-report"
            />);
            break;
          case REPORT_TYPES.studentUnit:
            reportToShow = (<QQStudentUnitReport
              reportType={reportType}
              unitId={subItemNormalized}
              studentId={itemIdNormalized}
              onTabChange={this.onTabChange}
              className="qq-report"
            />);

            break;
          case REPORT_TYPES.studentSubunit:
            reportToShow = (<QQStudentSubunitReport
              reportType={reportType}
              subunitId={subItemNormalized}
              studentId={itemIdNormalized}
              onTabChange={this.onTabChange}
              className="qq-report"
            />);

            break;
          case REPORT_TYPES.studentOverview:
            reportToShow = (<QQStudentOverviewV2
              reportType={reportType}
              onTabChange={this.onTabChange}
              studentId={itemIdNormalized}
              className="qq-report"
            />);
            break;
          default:
            reportToShow = <ReportError />;
        }
        break;
      default:
        reportToShow = <ReportError />;
    }

    const containerClass = clsx({
      [s.container]: true,
      [s.noData]: navigationBar.length === 0,
      [s.newCourseView]: true,
    });

    const reportsPage = (
      <div className={containerClass} >
        <div className={s.leftHandNavigation}>
          <div className={s.leftHandNavigationTitle}>Reports Dashboard</div>
          <div className={s.leftHandNavigationContent}>
            <LeftHandNav onTabChange={this.onTabChange} />
          </div>
        </div>
        <div className={s.reportContainer} ref={this.scrollRef}>
          {reportToShow}
        </div>
      </div>
    );

    return (
      <ReportsTabs
        match={this.props.match}
        location={location}
        onTabChange={this.onTabChange}
        reportToShow={reportsPage}
      >
        <ReportsContent
          match={this.props.match}
          onTabChange={this.onTabChange}
        />
      </ReportsTabs>
    );
  }
}

function mapStateToProps(state) {
  const courseId = get(state, 'courses.course.id', null);
  return {
    courseId,
    reports: state.reports,
    course: state.courses.course,
  };
}

const mapDispatchToProps = dispatch => bindActionCreators({
  setCurrentNavigationPath,
  getCourseAbstract,
  sendAnalyticsEvent,
  getCurrentUser,
  getCourse
}, dispatch);

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withLocation,
)(ReportsPage);
